import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, NotificationService, VesselService } from '@services';
import { Component, inject, signal } from '@angular/core';
import { DatasetFormAttachmentComponent } from '@components/dataset/form/attachment/attachment.component';
import { DatasetFormAttachmentCreateComponent } from '@components/dataset/form/attachment/create/create.component';
import { DatasetFormAttachmentDeleteComponent } from '@components/dataset/form/attachment/delete/delete.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormControlMarkdownComponent } from '@components/form-control/markdown/markdown.component';
import { HttpParams, httpResource } from '@angular/common/http';
import { HumanReadableSizePipe } from '@pipes';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatStepperModule } from '@angular/material/stepper';
import { PromptComponent } from '@components/prompt/prompt.component';
import { VesselHarbourFormComponent } from '@components/vessel/stepper/harbours/form.component';
import { VesselLinkFormComponent } from '@components/vessel/stepper/links/form.component';
import { VesselOwnerFormComponent } from '@components/vessel/stepper/owners/form.component';
import { VesselRegistrationFormComponent } from '@components/vessel/stepper/registrations/form.component';
import { environment } from '@environment';


import {
  Attachment,
  AttachmentListResponse,
  Harbour,
  Link,
  Permission,
  Registration,
  VesselDetails,
  VesselEntry,
  VesselOwner,
} from '@models';

@Component({
  selector: 'app-vessel-stepper-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    FormControlMarkdownComponent,
    HumanReadableSizePipe,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatPaginatorModule,
    MatStepperModule,
    ReactiveFormsModule,
  ],
})
export class VesselStepperFormComponent {
  // Routing
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  vesselService = inject(VesselService);

  // Stepper & form configuration
  private dialog = inject(MatDialog);

  isLinear = signal<boolean>(false);
  formFieldAppearance = signal<MatFormFieldAppearance>('outline');
  currentYear = signal(new Date().getUTCFullYear());

  // Validation rules

  reqValid = { validators: [Validators.required] };

  // Vessel Data

  vesselEntry = signal<VesselEntry>(this.route.snapshot.data['vessel']);
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  ///////////
  // FORMS //
  ///////////

  // Vessel Form

  name: FormControl = new FormControl(this.vesselEntry().content.name, this.reqValid);
  type: FormControl = new FormControl(this.vesselEntry().content?.type, {});
  description: FormControl = new FormControl(this.vesselEntry().content?.description, {});

  vesselForm: FormGroup = new FormGroup({
    name: this.name,
    type: this.type,
    description: this.description,
  });

  // Owner Form

  openOwnerForm(owner?: VesselOwner) {
    const dialogRef = this.dialog.open(VesselOwnerFormComponent, {
      data: owner,
      disableClose: true,
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((updatedOwner: VesselOwner | null) => {
      if (!updatedOwner) {
        return;
      }

      const entry = this.vesselEntry();

      entry.content.owners = [...(entry.content.owners || [])];
      if (owner) {
        const index = entry.content.owners.indexOf(owner);
        if (index !== -1) {
          entry.content.owners[index] = updatedOwner;
        }
      } else {
        entry.content.owners.push(updatedOwner);
      }

      this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
        next: () => {
          this.vesselEntry.set(entry);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        }
      })
    });
  }

  ownerDelete(options: MatListOption[]) {
    const entry = this.vesselEntry();
    entry.content.owners = (entry.content?.owners || [])
      .filter(o => options.findIndex(opt => opt.value === o) === -1);

    this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
      next: () => {
        this.vesselEntry.set(entry);
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      }
    });
  }

  // Registration Form

  openRegistrationForm(registration?: Registration | null) {
    const dialogRef = this.dialog.open(VesselRegistrationFormComponent, {
      data: registration,
      disableClose: true,
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((updatedRegistration: Registration | null) => {
      if (!updatedRegistration) {
        return;
      }

      const entry = this.vesselEntry();

      entry.content.registrations = [...(entry.content.registrations || [])];
      if (registration) {
        const index = entry.content.registrations.indexOf(registration);
        if (index !== -1) {
          entry.content.registrations[index] = updatedRegistration;
        }
      } else {
        entry.content.registrations.push(updatedRegistration);
      }

      this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
        next: () => {
          this.vesselEntry.set(entry);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        }
      });
    });
  }

  registrationDelete(options: MatListOption[]) {
    const entry = this.vesselEntry();
    const registrations = entry.content?.registrations || [];

    entry.content.registrations = registrations
      .filter(r => options.findIndex(o => o.value === r) === -1);

    this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
      next: () => {
        // Update the signal with the newest entry data.
        this.vesselEntry.set(entry);
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  // Harbours Form

  openHarbourForm(harbour?: Harbour | null) {
    const dialogRef = this.dialog.open(VesselHarbourFormComponent, {
      data: harbour,
      disableClose: true,
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((updatedHarbour: Harbour | null) => {
      if (!updatedHarbour) {
        return;
      }

      const entry = this.vesselEntry();

      entry.content.harbours = [...(entry.content.harbours || [])];
      if (harbour) {
        const index = entry.content.harbours.indexOf(harbour);
        if (index !== -1) {
          entry.content.harbours[index] = updatedHarbour;
        }
      } else {
        entry.content.harbours.push(updatedHarbour);
      }

      this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
        next: () => {
          // Update the signal with the newest entry data.
          this.vesselEntry.set(entry);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        },
      });
    });
  }

  harbourDelete(options: MatListOption[]) {
    const entry = this.vesselEntry();
    const harbours = entry.content?.harbours || [];

    entry.content.harbours = harbours.filter(h => options.findIndex(o => o.value === h) === -1);

    this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
      next: () => {
        // Update the signal with the newest entry data.
        this.vesselEntry.set(entry);
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  // History Form

  constructionYear: FormControl = new FormControl(this.vesselEntry().content?.constructionYear, {});
  constructionPlace: FormControl = new FormControl(this.vesselEntry().content?.constructionPlace, {});
  history: FormControl = new FormControl(this.vesselEntry().content?.history, {});
  shipwreckedYear: FormControl = new FormControl(this.vesselEntry().content?.shipwreckedYear, {});
  shipwreckedLocation: FormControl = new FormControl(this.vesselEntry().content?.shipwreckedLocation, {});

  historyForm: FormGroup = new FormGroup({
    constructionYear: this.constructionYear,
    constructionPlace: this.constructionPlace,
    history: this.history,
    shipwreckedYear: this.shipwreckedYear,
    shipwreckedLocation: this.shipwreckedLocation,
  });

  // Sources Form

  sources: FormControl = new FormControl(this.vesselEntry().content?.sources, {});
  caption: FormControl = new FormControl(this.vesselEntry().content?.caption, {});

  sourcesForm: FormGroup = new FormGroup({
    sources: this.sources,
    caption: this.caption,
  });

  // Links Form

  openLinkForm(link?: Link | null) {
    const dialogRef = this.dialog.open(VesselLinkFormComponent, {
      data: link,
      disableClose: true,
      minWidth: '360px',
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((updatedLink: Link | null) => {
      if (!updatedLink) {
        return;
      }

      const entry = this.vesselEntry();

      entry.content.links = [...(entry.content.links || [])];
      if (link) {
        const index = entry.content.links.indexOf(link);
        if (index !== -1) {
          entry.content.links[index] = updatedLink;
        }
      } else {
        entry.content.links.push(updatedLink);
      }

      this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
        next: () => {
          // Update the signal with the newest entry data.
          this.vesselEntry.set(entry);
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes');
        },
      });
    });
  }

  linkDelete(options: MatListOption[]) {
    const entry = this.vesselEntry();
    const links = entry.content?.links || [];

    entry.content.links = links.filter(l => options.findIndex(o => o.value === l) === -1);

    this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
      next: () => {
        // Update the signal with the newest entry data.
        this.vesselEntry.set(entry);
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes');
      },
    });
  }

  // Image Form

  defaultImagePageSize = 10;

  imagePage = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  imagePageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || this.defaultImagePageSize);
  imagePageSizeOptions = [this.defaultImagePageSize, 25, 50, 100];
  images = httpResource<AttachmentListResponse>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.imagePage() * this.imagePageSize())
      .set('take', this.imagePageSize())
      .set('count', true)
      .set('recordId', this.vesselEntry().id)
      .set('order', 'created:asc')

    return `${environment.kinkoHost}/dataset/${environment.vesselDatasetId}/attachment/?${httpParams.toString()}`;
  });

  onImagePageChange(event: PageEvent) {
    this.imagePage.set(event.pageIndex)
    this.imagePageSize.set(event.pageSize)
  }

  imageUpload(event: Event) {
    const element = event.target as HTMLInputElement;
    const files: FileList | null = element.files;
    if (!files) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentCreateComponent, {
      data: {
        datasetId: this.vesselEntry().datasetId,
        files: files,
        recordId: this.vesselEntry().id,
        prefix: '/images/'
      },
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe(() => {
      element.value = '';
      this.images.reload();
    });
  }

  imageEdit(items: MatListOption[]) {
    const ids = items.map((o) => o.value as string)
    const attachments = this.images.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    const dialogRef = this.dialog.open(DatasetFormAttachmentComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((updatedAttachments: Attachment[]) => {
      if (!updatedAttachments) {
        return;
      }
      this.images.reload();
    });
  }

  imageDelete(items: MatListOption[]) {
    const ids = items.map((o) => o.value as string)
    const attachments = this.images.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentDeleteComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe((deletedAttachments: Attachment[]) => {
      if (!deletedAttachments?.length) {
        return;
      }
      this.images.reload();
    })
  }

  // Submit updates to the back-end

  submit() {
    const entry = this.vesselEntry();

    // Merge updated vesselForm data.

    if (!this.vesselForm.pristine && this.vesselForm.valid) {
      const formData = {
        name: this.vesselForm.value.name?.trim(),
        type: this.vesselForm.value.type?.trim(),
        description: this.vesselForm.value.description?.trim(),
      } as VesselDetails;

      entry.content = { ...entry.content, ...formData };
    }

    // Merge updated historyForm data.

    if (!this.historyForm.pristine && this.historyForm.valid) {
      const formData = {
        constructionYear: this.historyForm.value.constructionYear || undefined,
        constructionPlace: this.historyForm.value.constructionPlace?.trim() || undefined,
        history: this.historyForm.value.history?.trim() || undefined,
        shipwreckedYear: this.historyForm.value.shipwreckedYear || undefined,
        shipwreckedLocation: this.historyForm.value.shipwreckedLocation?.trim() || undefined,
      } as VesselDetails;

      entry.content = { ...entry.content, ...formData };
    }

    if (!this.sourcesForm.pristine && this.sourcesForm.valid) {
      const formData = {
        sources: this.sourcesForm.value.sources?.trim() || undefined,
        caption: this.sourcesForm.value.caption?.trim() || undefined,
      } as VesselDetails;

      entry.content = { ...entry.content, ...formData };
    }

    // Save the updated vesselEntry

    this.vesselService.updateVessel(this.vesselEntry().id, entry).subscribe({
      next: () => {
        // Update the signal with the newest entry data.
        this.vesselEntry.set(entry);

        // Mark all forms as pristine
        this.vesselForm.markAsPristine();
        this.historyForm.markAsPristine();
        this.sourcesForm.markAsPristine();
        this.notificationService.info("Sucessfully saved changes.")
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  // Close editor. If any forms are dirty show a confirm dialog.
  onClose() {
    const pristine = this.vesselForm.pristine && this.historyForm.pristine &&
      this.sourcesForm.pristine

    if (!pristine) {
      this.dialog.open(PromptComponent, {
        minWidth: '360px',
        width: '480px',
        data: {
          title: 'Unsaved data',
          content: 'If you continue these changes will be discarded.',
        },
      }).afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.router.navigate(['/vessel', this.vesselEntry().id]);
        }
      });
    } else {
      this.router.navigate(['/vessel', this.vesselEntry().id]);
    }
  }
}
